import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { updatePageMeta } from '../utils/seo';
import { TEXT_STYLES, BUTTON_STYLES } from '../constants/styles';
import EmailButton from '../components/shared/EmailButton';
import { socialLinks } from '../constants';
import { LinkedInIcon } from '../components/icons';

function About() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    updatePageMeta(
      'About - Stephanie Zhang',
      'UI/UX Designer creating thoughtful digital experiences. Learn more about my background, skills, and approach to design.'
    );
  }, []);

  return (
    <div className="w-full overflow-x-hidden bg-white min-h-screen pt-32 pb-16">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="prose prose-lg max-w-none">
          {/* Header */}
          <div className="mb-12 md:mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-normal text-[#0D0D0D] mb-6">
              About
            </h1>
          </div>

          {/* Main Content */}
          <div className="space-y-8 md:space-y-12">
            {/* Introduction */}
            <section>
              <p className={`${TEXT_STYLES.body} text-lg md:text-xl leading-relaxed`}>
                I'm a designer and developer crafting clear, thoughtful digital experiences from concept to code. I blend product thinking, visual design, and engineering to build interfaces that are intuitive, scalable, and impactful.
              </p>
            </section>

            {/* Background */}
            <section>
              <div className={`${TEXT_STYLES.body} space-y-4`}>
                <p>
                  I’m a Vancouver-based designer and developer with a background in traditional art and a soft spot for both pixels and paint. With roots in economics, statistics, and visual studies, I like blending logic and creativity to build thoughtful, intuitive experiences. When I’m not designing or coding, you’ll probably find me playing competitive Call of Duty 🎮, listening to music 🎵, or <a href="https://drive.google.com/drive/folders/1KWwyRb0VZ6WPYxe9XzoSnlpm1y6-qt13?usp=sharing" target="_blank" rel="noopener noreferrer" className="underline underline-thin hover:no-underline">drawing</a> ✏️.
                </p>
              </div>
            </section>

            {/* Skills & Tools */}
            <section>
              <h2 className="text-2xl md:text-3xl font-playfair font-normal text-[#0D0D0D] mb-6">
                Skills & Tools
              </h2>
              <div className="space-y-6">
                {/* Design & UX Skills */}
                <div>
                  <h3 className="text-lg font-medium text-[#0D0D0D] mb-3">Design &amp; UX</h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'UX Strategy',
                      'Information Architecture',
                      'Wireframing & Prototyping',
                      'Design Systems',
                      'Component Libraries',
                      'Style Guides',
                      'Accessibility (WCAG 2.1)',
                      'Usability Testing',
                      'A/B Testing',
                      'Interaction Design',
                      'Typography',
                      'Visual Hierarchy',
                    ].map((skill, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-0.5 text-xs md:text-sm font-normal rounded bg-[#E5E5E5] text-[#666666]"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Development Skills */}
                <div>
                  <h3 className="text-lg font-medium text-[#0D0D0D] mb-3">Development</h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'HTML5',
                      'CSS3',
                      'JavaScript (ES6+)',
                      'React',
                      'Next.js',
                      'Tailwind CSS',
                      'WordPress (Custom Themes)',
                      'Shopify Liquid',
                    ].map((skill, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-0.5 text-xs md:text-sm font-normal rounded bg-[#E5E5E5] text-[#666666]"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Tools */}
                <div>
                  <h3 className="text-lg font-medium text-[#0D0D0D] mb-3">Tools</h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'Illustrator',
                      'Photoshop',
                      'InDesign',
                      'After Effects',
                      'Premiere Pro',
                      'Figma',
                      'Sketch',
                      'Zeplin',
                      'Git',
                      'GitHub/GitLab',
                      'Trello',
                      'Cursor',
                      'Co-pilot',
                    ].map((tool, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-0.5 text-xs md:text-sm font-normal rounded bg-[#E5E5E5] text-[#666666]"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-[#F7F7F7] rounded-lg p-8 md:p-12 mt-12">
              <div className="text-center space-y-6">
                <h2 className="text-2xl md:text-3xl font-playfair font-normal text-[#0D0D0D]">
                  Let's Connect
                </h2>
                <p className={`${TEXT_STYLES.body} text-base md:text-lg max-w-2xl mx-auto`}>
                  I'm always open to discussing new projects, design opportunities, or just connecting with fellow designers 
                  and developers. Feel free to reach out!
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                  <EmailButton 
                    showIcon={false}
                    className={BUTTON_STYLES.primary}
                  >
                    <span className="material-symbols-outlined text-xl">mail</span>
                    <span>Email</span>
                  </EmailButton>
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={BUTTON_STYLES.secondary}
                  >
                    <LinkedInIcon />
                    <span>LinkedIn</span>
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;
